import {
  AttributionControl,
  MapContainer,
  MapContainerProps,
  TileLayer,
  ZoomControl,
} from "react-leaflet";
import CombinedMarkers from "./combined-markers";
import DetourLines from "./detour-lines";
import { useMemo } from "react";
import { captureException } from "@sentry/react";

interface IFase {
  name: string;
  slug: string;
  status: string;
  description: string;
  beginDate: string;
  beginDateAlternative: string;
  endDate: string;
  endDateAlternative: string;
  blockedTransportations: {
    id: string;
    name: string;
  }[];
  limitedTransportations: {
    id: string;
    name: string;
  }[];
  availableTransportations: {
    id: string;
    name: string;
  }[];
  areas: {
    name: string;
    type: string;
    geometry: {
      type: string;
      coordinates: number[][];
    };
  }[];
  detourlines: {
    id: string;
    refinedGeometry: {
      type: string;
      coordinates: number[][];
    };
    type: string;
    directionalImpact: number;
  }[];
}

export type DetourTypeName = "Evenementen" | "Werkzaamheden" | "Spoor";
export type DetourStatus =
  | "In uitvoering"
  | "Op de planning"
  | "Verwachte werkzaamheden";

export interface IDetour {
  id: string;
  name: string;
  description: string;
  slug: string;
  website: string;
  status: DetourStatus;
  infraManager: any | null; // unknown
  detourType: {
    id: string;
    name: DetourTypeName;
  };
  coverPhoto: {
    url: string;
    slug: string;
  };
  fases: IFase[];
}

export interface IPOI {
  id: string;
  name: string;
  description: string;
  slug: string;

  geometry: {
    type: string;
    coordinates: number[];
    geometries: {
      type: string;
      coordinates: number[];
    }[];
  };
  coverPhoto: {
    url: string;
    slug: string;
  };
  type: {
    id: string;
    name: string;
    styling: {
      icon: string;
      color: string;
      fillColor: string;
      backgroundColor: string;
    };
  };
}

export default function Map({
  detours,
  pois,
  fase = null,
  showOthers = true,
  ...props
}: {
  detours: IDetour[];
  pois: IPOI[];
  showOthers?: boolean;
  fase?: number | null;
} & MapContainerProps) {
  // round robin per session a tile url for the user and store it in session storage
  const tileUrl = useMemo(() => {
    try {
      if (sessionStorage.getItem("tileUrl")) {
        return sessionStorage.getItem("tileUrl") as string;
      }
    } catch (error) {
      captureException(error);
    }

    // pick a random url form the list
    const tileUrls = [
      "https://api.mapbox.com/styles/v1/amsterdambereikbaar/cm0l07lqq008m01pmcgkx4mup/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiYW1zdGVyZGFtYmVyZWlrYmFhciIsImEiOiJjbTBsMGNnNDUwMGdvMmtzM294Nnhnb2o5In0.xbGFiR7PS4Xt-G3GTCfoQA",
      "https://api.mapbox.com/styles/v1/opwegnaarzes/clyehlu6m00qm01nw93ld5fm3/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1Ijoib3B3ZWduYWFyemVzIiwiYSI6ImNseWVoaTl1NDAyMWMyaXNleDI0NHI5dXUifQ.8txk5oGv0w0Mn_7PywMIFQ",
      "https://api.mapbox.com/styles/v1/sijmenjeromkok/cldbp61vv004k01p5stxwuntg/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1Ijoic2lqbWVuamVyb21rb2siLCJhIjoiY2xuOG1oOXN1MDA3ZTJtbzZvaG9xNDFsNSJ9.tWQHCc6HdONRCxgKLcUklQ",
    ];

    const randomIndex = Math.floor(Math.random() * tileUrls.length);
    const randomTileUrl = tileUrls[randomIndex];

    try {
      sessionStorage.setItem("tileUrl", randomTileUrl);
      return randomTileUrl;
    } catch (error) {
      captureException(error);
      return randomTileUrl;
    }
  }, []);

  const lines = detours
    ?.flatMap((detour) => {
      let filteredFases = detour.fases;
      if (fase) {
        filteredFases = [detour.fases[fase]];
      } else {
        // find first active fase
        const fase = detour.fases.find(
          (fase: IFase) =>
            fase.detourlines.length &&
            [
              "In uitvoering",
              "Op de planning",
              "Verwachte werkzaamheden",
            ].includes(fase.status)
        );
        if (fase) {
          filteredFases = [fase];
        }
      }

      return filteredFases.flatMap((fase) =>
        fase.detourlines.map((line) => ({
          ...line,
          detourId: detour.id,
        }))
      );
    })
    .map((line) => ({
      ...line,
      geometry: line.refinedGeometry,
    }));

  return (
    <MapContainer
      zoomControl={false}
      zoom={13}
      attributionControl={false}
      scrollWheelZoom={false}
      className="w-full"
      {...props}
    >
      {process.env.NODE_ENV === "development" ? (
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      ) : (
        <TileLayer url={tileUrl} />
      )}

      <AttributionControl
        prefix={`<a class="embed-map-link" href="https://www.omleidingennet.nl">Omleidingennet.nl</a><span class="embed-map-link"> by </span><a class="embed-map-link" href="https://www.savvy.codes">Savvy.codes</a><span class="embed-map-link"> &#124; <a class="embed-map-link" href="https://leafletjs.com/">Leaflet</a> &#124; &copy; <a class="embed-map-link" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors</span>`}
      />
      <ZoomControl position="bottomright" />
      {detours.length + lines.length > 0 && (
        <DetourLines
          detours={detours}
          lines={lines}
          visibleAtZoomLevel={showOthers ? null : "any"}
          hidePopup={!showOthers}
        />
      )}
      {detours.length + pois.length > 0 && (
        <CombinedMarkers detours={detours} lines={lines} pois={pois} />
      )}
    </MapContainer>
  );
}
